import React, { useCallback, useEffect, useState } from 'react';
import { Loader2, Search, Truck } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import apiClient from '@/services/apiClient';
import { errMsg, fmtDate, fmtQ } from './srUtils';

const Chip = ({ tone, children, testId }) => (
  <span data-testid={testId} className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium ${tone}`}>{children}</span>
);

const DeliveryCard = ({ d, selected, onPick }) => (
  <button type="button" onClick={() => onPick(d)} data-testid={`sr-delivery-${d.entry_id}`}
    className={`w-full text-left rounded-lg border p-3 transition-[border-color,background-color,box-shadow] duration-150 ${selected ? 'border-[#B45309] bg-amber-50 ring-1 ring-[#B45309]' : 'border-[#E5DFD0] bg-white hover:border-[#B45309]/50'}`}>
    <div className="flex items-center justify-between gap-2">
      <span className="font-mono font-semibold text-sm text-[#1A1F2E] truncate">{d.document_barcode || d.entry_id}</span>
      <span className="text-xs text-gray-500 shrink-0">{d.received_bundles} bundles</span>
    </div>
    <p className="text-sm text-gray-700 truncate">{d.vendor_name}</p>
    <p className="text-xs text-gray-500">{d.vendor_doc_no ? `Invoice ${d.vendor_doc_no} · ` : ''}{fmtDate(d.received_at)}</p>
    <div className="flex flex-wrap gap-1.5 mt-1.5">
      {d.not_on_po_bundles > 0 && <Chip tone="bg-amber-100 text-amber-800">{d.not_on_po_bundles} bundle(s) not on a PO</Chip>}
      {d.over_po_lines > 0 && <Chip tone="bg-red-100 text-red-700">{d.over_po_lines} PO line(s) above the PO</Chip>}
      {d.pending_returns > 0 && <Chip tone="bg-blue-100 text-blue-700">{d.pending_returns} waiting approval</Chip>}
      {d.returned_qty > 0 && <Chip tone="bg-gray-100 text-gray-700">{fmtQ(d.returned_qty)} pcs returned</Chip>}
    </div>
  </button>
);

/** Supplier deliveries received in the section — pick one to return goods from. */
export const DeliveryList = ({ section, selectedId, onPick, refreshKey }) => {
  const [list, setList] = useState(null);
  const [q, setQ] = useState('');
  const [onlyExcess, setOnlyExcess] = useState(true);

  const load = useCallback(() => {
    setList(null);
    apiClient.get('/supplier-returns/deliveries', { params: { section, q, only_excess: onlyExcess } })
      .then((r) => setList(r.data.deliveries || []))
      .catch((e) => { setList([]); toast.error(errMsg(e, 'Failed to load deliveries')); });
  }, [section, q, onlyExcess]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load, refreshKey]);

  return (
    <section className="space-y-3" data-testid="sr-delivery-list">
      <h2 className="text-sm font-semibold text-[#1A1F2E] flex items-center gap-2"><Truck className="w-4 h-4" />1. Pick the supplier delivery</h2>
      <div className="relative">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search document, supplier, invoice no" className="pl-9 h-11" data-testid="sr-delivery-search" />
      </div>
      <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
        <Switch checked={onlyExcess} onCheckedChange={setOnlyExcess} data-testid="sr-only-excess-switch" />
        Only deliveries with goods not on a PO or above the PO
      </label>
      {list === null ? <Loader2 className="w-6 h-6 animate-spin text-[#B45309] mx-auto" />
        : list.length === 0 ? <p className="text-sm text-gray-500 py-6 text-center" data-testid="sr-no-deliveries">No supplier deliveries {onlyExcess ? 'with excess goods ' : ''}found.</p>
          : <div className="grid gap-2 max-h-[65vh] overflow-y-auto pr-1">{list.map((d) => <DeliveryCard key={d.entry_id} d={d} selected={selectedId === d.entry_id} onPick={onPick} />)}</div>}
    </section>
  );
};
